import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLang } from '@/hooks/useLang';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articleById, articles } from '@/data/articles';
import { getArticleContent } from '@/data/articles/content';
import { uiContent } from '@/data/ui';
import { MAIN_SITE_URL } from '@/data/categories';
import type { ArticleContent } from '@/data/articles/types';
import { useSeo, SITE_URL, SITE_NAME, SITE_LOGO_URL } from '@/lib/seo';
import { useJsonLd } from '@/lib/jsonld';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const lang = useLang();
  const t = uiContent[lang];
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article = slug ? articleById(slug) : undefined;
  const content: ArticleContent | undefined = slug ? getArticleContent(slug, lang) : undefined;

  useSeo({
    title: content?.metaTitle ?? 'Article Not Found',
    description: content?.metaDescription ?? 'Explore AllChinaBuy Spreadsheet resources and shopping guides.',
    keywords: content ? `${content.title}, AllChinaBuy, AllChinaBuy spreadsheet` : undefined,
    canonicalPath: article ? `/article/${article.slug}` : undefined,
    ogType: 'article',
    ogTitle: content?.metaTitle,
    ogDescription: content?.metaDescription,
    ogImage: article?.image,
    ogSiteName: SITE_NAME,
    twitterTitle: content?.metaTitle,
    twitterDescription: content?.metaDescription,
    twitterImage: article?.image,
  });

  useJsonLd('article-jsonld', article && content ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.metaTitle,
    description: content.metaDescription,
    image: [article.image],
    datePublished: article.date,
    dateModified: article.date,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/article/${article.slug}` },
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: SITE_LOGO_URL },
    },
  } : null);

  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prev;
  }, [slug]);

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">404</h1>
          <p className="mt-3 text-foreground-600">Article not found.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50">
            <i className="ri-arrow-left-line text-base"></i>
            {t.backHome}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = article.related
    .map((s) => articles.find((a) => a.slug === s))
    .filter((a): a is NonNullable<typeof a> => Boolean(a));

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto max-w-4xl px-4 md:px-6 pt-24 md:pt-28 pb-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="Breadcrumb">
          <Link to="/" className="transition hover:text-primary-700">{t.breadcrumbHome}</Link>
          <i className="ri-arrow-right-s-line text-base"></i>
          <span className="text-foreground-400">{t.categoryLabels[article.category] ?? article.category}</span>
        </nav>

        {/* Article header */}
        <article className="mt-6">
          <div className="overflow-hidden rounded-lg">
            <img src={article.image} alt={content.h1} title={`${content.title} - AllChinaBuy Spreadsheet`} className="h-64 w-full object-cover object-top md:h-80" />
          </div>

          <div className="mt-6 flex items-center gap-3 text-sm text-foreground-500">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
              <i className={`${article.icon} text-base`}></i>
            </span>
            <span className="font-medium text-secondary-700">{t.categoryLabels[article.category] ?? article.category}</span>
            <span className="text-foreground-300">•</span>
            <span>{t.published} {article.date}</span>
          </div>

          <h1 className="mt-4 text-2xl font-extrabold leading-tight text-foreground-950 md:text-4xl">{content.h1}</h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-700 md:text-lg">{content.intro}</p>

          {content.sections.map((section, i) => (
            <section key={i} className="mt-10">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{section.heading}</h2>
              {section.paragraphs.map((p, j) => (
                <p key={j} className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{p}</p>
              ))}
              {section.list && (
                <ul className="mt-4 space-y-2">
                  {section.list.map((item, k) => (
                    <li key={k} className="flex items-start gap-2.5 text-sm text-foreground-600 md:text-base">
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-check-line text-sm"></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {content.comparison && (
            <section className="mt-10">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{content.comparison.title}</h2>
              <div className="mt-4 overflow-x-auto rounded-lg border border-background-200 bg-background-50">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-100">
                      {content.comparison.columns.map((col, i) => (
                        <th key={i} className="px-4 py-3 font-semibold text-foreground-900">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {content.comparison.rows.map((row, i) => (
                      <tr key={i} className="border-b border-background-100 last:border-0">
                        {row.map((cell, j) => (
                          <td key={j} className={`px-4 py-3 ${j === 0 ? 'font-medium text-foreground-800' : 'text-foreground-600'}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* FAQ */}
          {content.faqs.length > 0 && (
            <section className="mt-10">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{t.nav.faq}</h2>
              <div className="mt-4 space-y-3">
                {content.faqs.map((faq, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={i} className="rounded-lg border border-background-200 bg-background-50">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(open ? null : i)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                      >
                        <span className="text-sm font-semibold text-foreground-900">{faq.q}</span>
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition ${open ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line text-base"></i>
                        </span>
                      </button>
                      {open && (
                        <div className="px-4 pb-4">
                          <p className="text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Disclaimer */}
          <p className="mt-10 rounded-lg border border-background-200 bg-background-100/60 p-4 text-xs leading-relaxed text-foreground-500">
            {t.articleDisclaimer}
          </p>

          {/* CTA to main site */}
          <div className="mt-8 flex flex-col items-center gap-3 rounded-lg bg-secondary-100 p-6 text-center sm:flex-row sm:justify-between sm:text-left">
            <p className="text-sm font-medium text-foreground-800">{t.exploreResources}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              {t.visitMain}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{t.relatedResources}</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.slug} article={a} lang={lang} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}